import type {
  CabinetSummary,
  SummaryBookingsCard,
  SummaryFreeSlotsCard,
  SummaryLoadCard,
  SummaryLossesCard,
  SummaryLossRow,
  SummaryRevenueCard,
} from "@planerius/contracts/cabinet"

import { formatDayMonth, formatTimeOfDay, todayInZone } from "../../lib/format"
import { plural } from "../../lib/plural"
import { formatMoney } from "../directories/format-money"

/**
 * 5-В1 «Сводка владельца» как данные, отдельно от разметки.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭКРАНА: НОЛЬ — НЕ ОТВЕТ НА ВОПРОС «ПОСЧИТАЛОСЬ ЛИ». Карточка,
 * которая не посчиталась, показывает отказ словами и кнопку «Повторить», а не «0».
 * «Выручка 0 ₽» владелец читает как разорение, а не как сбой, и звонит в поддержку
 * в тот же вечер. Поэтому все значения здесь `number | null`, и null рисуется
 * прочерком с объяснением, а не нулём.
 *
 * ВТОРОЕ ПРАВИЛО: КАЖДОЕ ЧИСЛО ПОДПИСАНО ТЕМ, ЧТО С НИМ ДЕЛАТЬ. «12 записей» ничего
 * не говорит; «12 записей, свободно 21 ч — ещё можно продать» говорит всё. Подписи
 * взяты из макета дословно: это решение дизайнера о том, как объяснять цифры.
 */

/**
 * «за сегодня», «за 22 сентября» — день, за который посчитана карточка.
 *
 * ЗАГОЛОВОК НЕ ИМЕЕТ ПРАВА ГОВОРИТЬ «СЕГОДНЯ» ПРО ЧУЖОЙ ДЕНЬ. Ссылку на сводку
 * пересылают в переписке («посмотри, что вчера было на Гагарина»), и человек, открывший
 * её, читает крупный заголовок, а не мелкую дату под ним: «Выручка за сегодня 38 400 ₽»
 * над позавчерашними цифрами он доложит владельцу как сегодняшнюю.
 */
export function dayLabel(date: string, timezone: string): string {
  return date === todayInZone(timezone) ? "за сегодня" : `за ${formatDayMonth(date, timezone)}`
}

/** «9 ч 05 мин», «45 мин», «0 мин» — время салона читается часами, а не минутами. */
export function hoursLabel(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes))
  const hours = Math.floor(safe / 60)
  const rest = safe % 60
  if (hours === 0) return `${String(rest)} мин`
  if (rest === 0) return `${String(hours)} ч`
  return `${String(hours)} ч ${String(rest).padStart(2, "0")} мин`
}

/** «+12 %», «−7 %», «без изменений». Знак минуса — настоящий, а не дефис. */
export function deltaLabel(delta: number | null, unit: "%" | "пункт"): string | null {
  if (delta === null) return null
  if (delta === 0) return "без изменений"
  const sign = delta > 0 ? "+" : "−"
  const value = Math.abs(delta)
  if (unit === "%") return `${sign}${String(value)} %`
  return `${sign}${String(value)} ${plural(value, "пункт", "пункта", "пунктов")}`
}

/** Рост это хорошо не везде: у потерь «+3» — плохая новость. */
export type DeltaTone = "good" | "bad" | "flat"

export function deltaTone(delta: number | null, growthIsGood: boolean): DeltaTone {
  if (delta === null || delta === 0) return "flat"
  const good = delta > 0 ? growthIsGood : !growthIsGood
  return good ? "good" : "bad"
}

export interface CardView {
  readonly title: string
  /** Главное число строкой. null — не посчиталось. */
  readonly value: string | null
  readonly delta: string | null
  readonly deltaTone: DeltaTone
  /** Подпись под числом: что с ним делать. */
  readonly caption: string | null
  /** Карточка не посчиталась — показывается отказ и «Повторить». */
  readonly failed: boolean
}

/** Отказ карточки — дословно из макета. */
export const CARD_FAILED = "Не посчиталась — остальное актуально."

export function bookingsCard(
  card: SummaryBookingsCard,
  when: "today" | "tomorrow",
  day: string,
): CardView {
  const title = when === "today" ? `Записей ${day}` : "Записей на следующий день"
  if (card.status === "failed" || card.count === null) {
    return { title, value: null, delta: null, deltaTone: "flat", caption: null, failed: true }
  }

  const parts: string[] = []
  if (when === "today") {
    if (card.pending !== null) {
      parts.push(`ждут подтверждения ${String(card.pending)}`)
    }
    if (card.online !== null && card.online > 0) {
      parts.push(`из них ${String(card.online)} записались онлайн`)
    }
  } else if (card.freeMinutes !== null && card.busyMinutes !== null) {
    // Подпись из макета — это не украшение, а весь смысл завтрашней карточки:
    // владелец открывает её вечером, чтобы решить, кому звонить.
    parts.push(
      card.freeMinutes > card.busyMinutes
        ? "Ещё можно продать: свободных часов больше, чем занято"
        : "Занято больше, чем свободно — день почти собран",
    )
  }

  const delta =
    when === "today"
      ? card.arrived === null
        ? null
        : `пришли ${String(card.arrived)}`
      : card.freeMinutes === null
        ? null
        : `свободно ${hoursLabel(card.freeMinutes)}`

  return {
    title,
    value: String(card.count),
    delta,
    // «Пришли 9» и «свободно 21 ч» — это факты дня, а не рост или падение.
    deltaTone: "flat",
    caption: parts.length > 0 ? parts.join(" · ") : null,
    failed: false,
  }
}

export function loadCard(card: SummaryLoadCard, day: string): CardView {
  const title = `Загрузка мастеров ${day}`
  if (card.status === "failed" || card.percent === null) {
    return { title, value: null, delta: null, deltaTone: "flat", caption: null, failed: true }
  }
  // В подписи — крайние: самый загруженный и самый свободный. Средняя цифра сама
  // по себе не говорит, к кому идти разговаривать.
  const sorted = [...card.rows].sort((a, b) => b.percent - a.percent)
  const top = sorted[0]
  const bottom = sorted[sorted.length - 1]
  // Сравнение по СТРОКЕ, а не по человеку: один мастер работает в двух точках, и это
  // две разные строки с разной загрузкой. Пока сравнивали по employeeId, самый
  // свободный пропадал из подписи ровно там, где он и есть предмет разговора.
  const same = top !== undefined && bottom !== undefined && top === bottom
  const caption =
    top === undefined
      ? null
      : same || bottom === undefined
        ? `${top.displayName} ${String(top.percent)} %`
        : `${bottom.displayName} ${String(bottom.percent)} % · ${top.displayName} ${String(top.percent)} %`

  return {
    title,
    value: `${String(card.percent)} %`,
    delta: deltaLabel(card.deltaPercent, "пункт"),
    deltaTone: deltaTone(card.deltaPercent, true),
    caption: caption === null ? null : `${caption} · к прошлой неделе`,
    failed: false,
  }
}

export function revenueCard(card: SummaryRevenueCard, day: string): CardView {
  const title = `Выручка ${day}`
  if (card.status === "failed" || card.minor === null) {
    return { title, value: null, delta: null, deltaTone: "flat", caption: null, failed: true }
  }
  // ПОДПИСЬ ОБЯЗАТЕЛЬНА И СТОИТ ПЕРВОЙ. Выручка по завершённым записям и касса —
  // разные числа, и владелец, сверивший их без этой строки, звонит с претензией.
  const previous =
    card.previousMinor === null
      ? null
      : `та же неделя назад: ${formatMoney(card.previousMinor) ?? "—"}`
  return {
    title,
    value: formatMoney(card.minor),
    delta: deltaLabel(card.deltaPercent, "%"),
    deltaTone: deltaTone(card.deltaPercent, true),
    caption: ["По завершённым записям, не по кассе", previous].filter((x) => x !== null).join(" · "),
    failed: false,
  }
}

export function lossesCard(card: SummaryLossesCard, day: string): CardView {
  const title = `Отмены и неявки ${day}`
  if (card.status === "failed" || card.count === null) {
    return { title, value: null, delta: null, deltaTone: "flat", caption: null, failed: true }
  }
  const parts: string[] = []
  if (card.lostMinutes !== null && card.lostMinutes > 0) {
    // Сгоревшее время — главное число карточки: три отмены по 20 минут и три по
    // два часа дают один и тот же счётчик «3», а день из них получается разный.
    parts.push(`Сгорело ${hoursLabel(card.lostMinutes)}`)
  }
  if (card.cancelledByClient !== null && card.cancelledByStaff !== null) {
    parts.push(
      `отмена клиентом ${String(card.cancelledByClient)}, салоном ${String(card.cancelledByStaff)}`,
    )
  }
  return {
    title,
    value: String(card.count),
    delta:
      card.noShowCount === null || card.noShowCount === 0
        ? null
        : `${String(card.noShowCount)} ${plural(card.noShowCount, "неявка", "неявки", "неявок")}`,
    deltaTone: card.noShowCount !== null && card.noShowCount > 0 ? "bad" : "flat",
    caption: parts.length > 0 ? parts.join(" · ") : null,
    failed: false,
  }
}

export function freeSlotsCard(card: SummaryFreeSlotsCard, timezone: string): CardView {
  const title = "Свободные окна · ближайшие 3 часа"
  if (card.status === "failed" || card.count === null) {
    return { title, value: null, delta: null, deltaTone: "flat", caption: null, failed: true }
  }
  const caption =
    card.slots.length === 0
      ? null
      : card.slots
          .slice(0, 4)
          .map((slot) => `${slot.employeeName} ${formatTimeOfDay(slot.startAt, timezone)}`)
          .join(" · ")
  return {
    title,
    value: String(card.count),
    delta: null,
    deltaTone: "flat",
    caption,
    failed: false,
  }
}

/**
 * Строка разбора потерь (5-В3).
 *
 * НЕЯВКА И ОТМЕНА НАЗЫВАЮТСЯ РАЗНЫМИ СЛОВАМИ. Клиент, отменивший накануне, и клиент,
 * не пришедший молча, — это два разных разговора, и салон обязан видеть, какой из них
 * ему предстоит. Отмена, которую правила салона засчитали как неявку, называется
 * неявкой и объясняется причиной — иначе строка выглядит обвинением без основания.
 */
export function lossKindLabel(row: SummaryLossRow): string {
  if (row.kind === "no_show") {
    return row.reason === null ? "неявка" : `неявка · ${row.reason.toLowerCase()}`
  }
  const by =
    row.cancelledBy === "client"
      ? "клиент"
      : row.cancelledBy === "staff"
        ? "салон"
        : row.cancelledBy === "system"
          ? "система"
          : null
  return by === null ? "отмена" : `отмена · ${by}`
}

export function lossRowCaption(row: SummaryLossRow, timezone: string): string {
  const parts: string[] = []
  if (row.employeeName !== null) parts.push(row.employeeName)
  if (row.serviceNames.length > 0) parts.push(row.serviceNames.join(", "))
  // Цена приходит только с finance.view: без него строка просто не называет денег,
  // а не показывает «0 ₽».
  const money = row.priceMinor === null ? null : formatMoney(row.priceMinor)
  if (money !== null) parts.push(money)
  if (row.kind === "no_show") {
    parts.push(
      row.clientNoShowCount <= 1
        ? "первая"
        : `${String(row.clientNoShowCount)}-я неявка клиента`,
    )
  }
  if (row.quarantineUntil !== null) {
    // Время в карантине закрыто ТОЛЬКО для витрины: администратор вправе продать его
    // по телефону, и знать про это он должен из строки, а не из опыта.
    parts.push(`время в карантине до ${formatTimeOfDay(row.quarantineUntil, timezone)}`)
  }
  return parts.join(" · ")
}

/** Сноска разбора загрузки — дословно из макета 5-В2. */
export const LOAD_FOOTNOTE =
  "Занятое услугами время к рабочему времени смены. Нажатие на мастера открывает его день."

/**
 * Та же сноска БЕЗ обещания открыть день.
 *
 * Журнал закрыт правом, и у бухгалтера с наблюдателем его нет намеренно — там имена
 * клиентов. Обещать им нажатие значит вести в отказ с первого же экрана после входа.
 */
export const LOAD_FOOTNOTE_READONLY = "Занятое услугами время к рабочему времени смены."

/** Сноска разбора потерь — дословно из макета 5-В3. */
export const LOSSES_FOOTNOTE =
  "Отмена клиентом заранее и неявка — разные вещи; счётчики ведутся отдельно. Нажатие открывает запись."

export const LOSSES_FOOTNOTE_READONLY =
  "Отмена клиентом заранее и неявка — разные вещи; счётчики ведутся отдельно."

/** Числа есть, имён нет: человеку не положены персональные данные клиентов. */
export const LOSSES_NO_NAMES_TITLE = "Имена клиентов вам не открыты"
export const LOSSES_NO_NAMES_TEXT =
  "Ваша роль видит сводные числа без персональных данных клиентов. Сколько было отмен и неявок — на карточке выше; кто именно не пришёл, покажет владелец или управляющий."

/** Порог, ниже которого загрузка мастера подсвечивается тревожно (макет 5-В2). */
export const LOAD_LOW_PERCENT = 40

/** Первый день салона: цифр нет не потому, что сломалось. */
export const FIRST_DAY_TITLE = "Данных пока нет — салон работает первый день"
export const FIRST_DAY_TEXT = "Цифры появятся, как только пройдут первые визиты."

/** Режим бережного расхода ресурсов — словами, а не пустыми карточками. */
export const BOOKING_ONLY_TEXT =
  "Сводка временно недоступна: система бережёт ресурсы для записи клиентов. Журнал и запись работают."

/**
 * Пуст ли день целиком — то есть это первый день салона, а не сбой.
 *
 * Отличать обязательно: пустые карточки без объяснения читаются как поломка, и салон,
 * открывший кабинет утром первого дня, звонит в поддержку вместо того, чтобы работать.
 */
export function isFirstDay(summary: CabinetSummary): boolean {
  // ФАКТ С СЕРВЕРА, А НЕ ДОГАДКА ПО НУЛЯМ. Все нули бывают и у салона, работающего три
  // года: обычный выходной, будущая дата, пустая область филиалов. Пока «первый день»
  // выводился из них, экран сообщал работающему салону, что он открылся сегодня,
  // и прятал все шесть карточек.
  return summary.everWorked === false
}

/**
 * Пустой день работающего салона — это ответ, а не отсутствие данных.
 *
 * Отличать от «первого дня» обязательно: салону, который работает, надо сказать про
 * ЭТОТ день, а не про его историю.
 */
export function isQuietDay(summary: CabinetSummary): boolean {
  return (
    summary.everWorked === true &&
    summary.today.status === "ready" &&
    summary.today.count === 0 &&
    summary.load.status === "ready" &&
    summary.load.rows.length === 0
  )
}

export const QUIET_DAY_TITLE = "В этот день смен и записей не было"
export const QUIET_DAY_TEXT =
  "Выходной, праздник или день, который ещё не заполнен. Цифры появятся, когда на день встанет смена."

/** Ни одного филиала в области — это про доступ, а не про салон. */
export const NO_BRANCHES_TITLE = "Ни один филиал вам не открыт"
export const NO_BRANCHES_TEXT =
  "Сводка считается по филиалам вашей области, а их пока нет. Попросите владельца салона добавить вам точку."

